import { PageFilterPreferences } from '@enview/interface/types/OrganizationUser';
import isEmpty from 'lodash-es/isEmpty';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOrgIdForTeamMode } from '../dux';
import { State } from '../dux/@types';
import { updatePageFilterPreferences } from '../dux/AccountDux';

type FilterPage = keyof PageFilterPreferences;

type UsePageFilterPreferencesOptions<Page extends FilterPage> = {
  page: Page;
  /** False when the page does not use saved filters */
  enabled: boolean;
  /** True once the data `applyFilters` uses to validate saved filters has loaded */
  isReady: boolean;
  /**
   * Query param keys owned by the page. A URL that sets any of them is treated as
   * a shared or bookmarked link, so saved filters do not override it on load.
   */
  pageParamKeys: string[];
  /** Replaces the page's filters; `undefined` resets them to their defaults */
  applyFilters: (filters: PageFilterPreferences[Page]) => void;
};

type UsePageFilterPreferencesResult<Page extends FilterPage> = {
  /** False until saved filters have been applied for the first time */
  isHydrated: boolean;
  /**
   * Changes whenever filters are applied for a different organization. Use it as
   * a `key` on filter inputs that copy the query into local state.
   */
  filtersKey: string;
  saveFilters: (filters: NonNullable<PageFilterPreferences[Page]>) => void;
};

const hasPageParamsInUrl = (pageParamKeys: string[]): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return pageParamKeys.some((key) => urlParams.has(key));
};

const toLowerCaseSet = (abbreviations: string[]): Set<string> =>
  new Set(abbreviations.map((abbreviation) => abbreviation.toLowerCase()));

/**
 * Returns the jurisdictions to save, or `undefined` when the selection is empty
 * or covers every team jurisdiction. Saving no selection means restored filters
 * include jurisdictions the team gains later.
 */
export const getJurisdictionsToSave = (
  selected: string[],
  teamAbbreviations: string[],
): string[] | undefined => {
  if (selected.length === 0) return undefined;
  const selectedSet = toLowerCaseSet(selected);
  const coversAllTeamJurisdictions =
    teamAbbreviations.length > 0 &&
    teamAbbreviations.every((abbreviation) =>
      selectedSet.has(abbreviation.toLowerCase()),
    );
  return coversAllTeamJurisdictions ? undefined : selected;
};

/**
 * Returns the saved jurisdictions the team still has, or `undefined` (every team
 * jurisdiction) when none remain.
 */
export const getRestoredJurisdictions = (
  saved: string[] | undefined,
  teamAbbreviations: string[],
): string[] | undefined => {
  const teamSet = toLowerCaseSet(teamAbbreviations);
  const restored = saved?.filter((abbreviation) =>
    teamSet.has(abbreviation.toLowerCase()),
  );
  return restored?.length ? restored : undefined;
};

/**
 * Restores and saves a page's filters for the current organization.
 *
 * Saved filters are applied on first load unless the URL already sets the page's
 * params. They are applied again when the user switches organization, so one
 * organization's filters are never shown or saved under another.
 */
const usePageFilterPreferences = <Page extends FilterPage>(
  options: UsePageFilterPreferencesOptions<Page>,
): UsePageFilterPreferencesResult<Page> => {
  const { page, enabled, isReady, pageParamKeys, applyFilters } = options;
  const dispatch = useDispatch();

  // `undefined` while team memberships load, and `null` in the personal workspace
  const orgId: number | null | undefined = useSelector(getOrgIdForTeamMode);
  const savedFilters = useSelector((state: State) =>
    typeof orgId === 'number'
      ? state.account.organizationUser?.settings?.viewPreferences?.[orgId]?.[page]
      : undefined,
  );

  // The organization whose filters are applied; unset until the first hydration
  const [hydratedFor, setHydratedFor] = useState<{
    orgId: number | null | undefined;
  }>();

  useEffect(() => {
    if (!enabled || !isReady) return;
    if (hydratedFor && (!orgId || orgId === hydratedFor.orgId)) return;

    const isOrgSwitch = hydratedFor !== undefined && hydratedFor.orgId !== undefined;
    if (isOrgSwitch) {
      applyFilters(savedFilters);
    } else if (!hasPageParamsInUrl(pageParamKeys) && !isEmpty(savedFilters)) {
      applyFilters(savedFilters);
    }
    setHydratedFor({ orgId });
  }, [enabled, isReady, orgId]);

  const saveFilters = (filters: NonNullable<PageFilterPreferences[Page]>): void => {
    // The personal workspace has no organization to save filters under
    if (typeof orgId !== 'number') return;
    dispatch(updatePageFilterPreferences(orgId, page, filters));
  };

  return {
    isHydrated: !enabled || hydratedFor !== undefined,
    filtersKey: String(hydratedFor?.orgId),
    saveFilters,
  };
};

export default usePageFilterPreferences;
